import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export type MessageConversationKind = "direct" | "rooms";
export type MessageConversationFilter = "inbox" | "archived";

export interface MessageDirectoryMember {
  userId: string;
  display: string;
  handle: string;
  role: string;
  specialty: string | null;
  existingConversationId: string | null;
}

export interface MessageConversationIdentity {
  userId: string;
  display: string;
  handle: string;
  role: string;
  specialty: string | null;
}

export interface MessageFacilityIdentity {
  id: string;
  name: string;
}

export interface MessageConversationSummary {
  id: string;
  kind: MessageConversationKind;
  other: MessageConversationIdentity | null;
  facility: MessageFacilityIdentity | null;
  subject: string | null;
  lastMessage: {
    excerpt: string;
    at: string;
    mine: boolean;
  } | null;
  unread: number;
  muted: boolean;
  archived: boolean;
  blocked: boolean;
}

export interface MessageItem {
  id: string;
  body: string;
  mine: boolean;
  sender: {
    display: string;
    handle: string;
    role: string;
  };
  createdAt: string;
}

export interface MessagesUnreadResponse {
  unread: number;
}

export interface MessagesDirectoryResponse {
  members: MessageDirectoryMember[];
}

export interface MessagesConversationsResponse {
  conversations: MessageConversationSummary[];
  nextCursor: string | null;
}

export interface MessageConversationResponse {
  conversation: MessageConversationSummary;
  messages: MessageItem[];
  hasMore: boolean;
}

export interface CreateMessageConversationResponse {
  conversation: MessageConversationSummary;
  message: MessageItem;
}

export interface RoomsTargetResponse {
  facility: MessageFacilityIdentity;
  available: boolean;
  remainingToday: number;
}

export interface CreateRoomsMessageResponse {
  conversation: MessageConversationSummary;
  message: MessageItem;
  sent: true;
}

export interface MessagePreferences {
  messagesEnabled: boolean;
  emailNotify: boolean;
}

export interface MessageDayGroup {
  key: string;
  label: string;
  messages: MessageItem[];
}

export const MESSAGES_SAFETY_LINE =
  "Messages are for colleague conversations — never patient details. To refer, use Referrals.";
const MESSAGE_TIME_ZONE = "Australia/Sydney";

export function useMessagesUnread(citySlug: string) {
  return useQuery({
    queryKey: ["messages-unread", citySlug],
    queryFn: () =>
      api.get<MessagesUnreadResponse>(`/${citySlug}/v1/messages/unread`),
    enabled: Boolean(citySlug),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useMessagesDirectory(
  citySlug: string,
  query: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["messages-directory", citySlug, query.trim().toLowerCase()],
    queryFn: () =>
      api.get<MessagesDirectoryResponse>(`/${citySlug}/v1/messages/directory`, {
        params: { q: query.trim() || undefined },
      }),
    enabled: enabled && Boolean(citySlug),
  });
}

export function useMessageConversations(
  citySlug: string,
  filter: MessageConversationFilter,
) {
  return useInfiniteQuery({
    queryKey: ["messages-conversations", citySlug, filter],
    queryFn: ({ pageParam }) =>
      api.get<MessagesConversationsResponse>(
        `/${citySlug}/v1/messages/conversations`,
        {
          params: {
            filter,
            cursor: pageParam || undefined,
            limit: 30,
          },
        },
      ),
    initialPageParam: "",
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: Boolean(citySlug),
  });
}

export function useMessageConversation(
  citySlug: string,
  conversationId: string,
) {
  return useInfiniteQuery({
    queryKey: ["message-conversation", citySlug, conversationId],
    queryFn: ({ pageParam }) =>
      api.get<MessageConversationResponse>(
        `/${citySlug}/v1/messages/conversations/${encodeURIComponent(conversationId)}`,
        {
          params: {
            before: pageParam || undefined,
            limit: 50,
          },
        },
      ),
    initialPageParam: "",
    getNextPageParam: (page) =>
      page.hasMore ? page.messages[0]?.id : undefined,
    enabled: Boolean(citySlug && conversationId),
  });
}

export function useCreateMessageConversation(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { recipientUserId: string; body: string }) =>
      api.post<CreateMessageConversationResponse>(
        `/${citySlug}/v1/messages/conversations`,
        body,
      ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({
        queryKey: ["messages-conversations", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["message-conversation", citySlug, response.conversation.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["messages-unread", citySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useSendMessage(citySlug: string, conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string }) =>
      api.post<MessageItem>(
        `/${citySlug}/v1/messages/conversations/${encodeURIComponent(conversationId)}/messages`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["message-conversation", citySlug, conversationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["messages-conversations", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["messages-unread", citySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useUpdateMessageConversation(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      ...body
    }: {
      conversationId: string;
      muted?: boolean;
      archived?: boolean;
    }) =>
      api.patch<{ ok: true }>(
        `/${citySlug}/v1/messages/conversations/${encodeURIComponent(conversationId)}`,
        body,
      ),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["messages-conversations", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["message-conversation", citySlug, variables.conversationId],
      });
    },
  });
}

export function useBlockMessageUser(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { userId: string; blocked: boolean }) =>
      api.post<{ ok: true }>(`/${citySlug}/v1/messages/block`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["messages-conversations", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["message-conversation", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["messages-directory", citySlug],
      });
    },
  });
}

export function useRoomsTarget(
  citySlug: string,
  facilityId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["messages-rooms-target", citySlug, facilityId],
    queryFn: () =>
      api.get<RoomsTargetResponse>(
        `/${citySlug}/v1/messages/rooms-target/${encodeURIComponent(facilityId)}`,
      ),
    enabled: enabled && Boolean(citySlug && facilityId),
  });
}

export function useCreateRoomsMessage(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { facilityId: string; subject: string; body: string }) =>
      api.post<CreateRoomsMessageResponse>(
        `/${citySlug}/v1/messages/rooms`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["messages-conversations", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["messages-unread", citySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useMessagePreferences(citySlug: string) {
  return useQuery({
    queryKey: ["messages-preferences", citySlug],
    queryFn: () =>
      api.get<MessagePreferences>(`/${citySlug}/v1/messages/preferences`),
    enabled: Boolean(citySlug),
  });
}

export function useUpdateMessagePreferences(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MessagePreferences) =>
      api.patch<{ ok: true }>(`/${citySlug}/v1/messages/preferences`, body),
    onSuccess: (_response, preferences) => {
      queryClient.setQueryData<MessagePreferences>(
        ["messages-preferences", citySlug],
        preferences,
      );
      void queryClient.invalidateQueries({
        queryKey: ["messages-directory", citySlug],
      });
    },
  });
}

export function messageErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

export function isMessageIdentifierGuardError(error: unknown): boolean {
  return messageErrorCode(error) === "messages.identifier_detected";
}

export function messageErrorMessage(error: unknown): string {
  switch (messageErrorCode(error)) {
    case "messages.handle_required":
      return "Add your member handle in Board settings before sending a message.";
    case "messages.blocked":
      return "This conversation is blocked and cannot receive new messages.";
    case "messages.recipient_disabled":
      return "This colleague is not accepting new messages.";
    case "messages.rate_limited":
      return "You have reached the hourly message limit. Try again later.";
    case "messages.rooms_rate_limited":
      return "You have reached today’s rooms-message limit.";
    case "messages.rooms_unavailable":
      return "This facility cannot receive rooms messages.";
    case "messages.rooms_reply_by_email":
      return "Replies to this rooms conversation happen by email.";
    default:
      return "The message could not be sent. Check your connection and try again.";
  }
}

export function resolveHandlePreselect(
  members: ReadonlyArray<MessageDirectoryMember>,
  requestedHandle: string,
): MessageDirectoryMember | null {
  const handle = requestedHandle.trim().replace(/^@/, "").toLowerCase();
  if (!handle) return null;
  return (
    members.find((member) => member.handle.toLowerCase() === handle) ?? null
  );
}

export function sortConversationsUnreadFirst(
  conversations: ReadonlyArray<MessageConversationSummary>,
): MessageConversationSummary[] {
  return [...conversations].sort((left, right) => {
    const unreadDifference = Number(right.unread > 0) - Number(left.unread > 0);
    if (unreadDifference !== 0) return unreadDifference;
    const timeDifference =
      conversationTimestamp(right) - conversationTimestamp(left);
    if (timeDifference !== 0) return timeDifference;
    return left.id.localeCompare(right.id);
  });
}

export function formatMessageRelativeTime(
  value: string,
  now = new Date(),
): string {
  const date = new Date(value);
  const time = date.getTime();
  if (!Number.isFinite(time)) return value;
  const elapsedMinutes = Math.max(
    0,
    Math.floor((now.getTime() - time) / 60_000),
  );
  if (elapsedMinutes < 1) return "Just now";
  if (elapsedMinutes < 60) return `${elapsedMinutes}m`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24 && calendarKey(date) === calendarKey(now)) {
    return `${elapsedHours}h`;
  }
  if (calendarKey(date) === previousCalendarKey(now)) return "Yesterday";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year:
      calendarParts(date).year === calendarParts(now).year
        ? undefined
        : "numeric",
    timeZone: MESSAGE_TIME_ZONE,
  }).format(date);
}

export function groupMessagesByDay(
  messages: ReadonlyArray<MessageItem>,
  now = new Date(),
): MessageDayGroup[] {
  const groups: MessageDayGroup[] = [];
  for (const message of messages) {
    const date = new Date(message.createdAt);
    const key = Number.isFinite(date.getTime())
      ? calendarKey(date)
      : `invalid-${message.id}`;
    const current = groups[groups.length - 1];
    if (current?.key === key) {
      current.messages.push(message);
      continue;
    }
    groups.push({
      key,
      label: Number.isFinite(date.getTime())
        ? dayGroupLabel(date, now)
        : message.createdAt,
      messages: [message],
    });
  }
  return groups;
}

function conversationTimestamp(conversation: MessageConversationSummary): number {
  if (!conversation.lastMessage) return 0;
  const value = new Date(conversation.lastMessage.at).getTime();
  return Number.isFinite(value) ? value : 0;
}

function calendarKey(date: Date): string {
  const parts = calendarParts(date);
  return [parts.year, parts.month, parts.day].join("-");
}

function previousCalendarKey(date: Date): string {
  const parts = calendarParts(date);
  const previous = new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) - 1),
  );
  return [
    previous.getUTCFullYear(),
    String(previous.getUTCMonth() + 1).padStart(2, "0"),
    String(previous.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function calendarParts(date: Date): { year: string; month: string; day: string } {
  const parts = new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: MESSAGE_TIME_ZONE,
  }).formatToParts(date);
  const value = (type: "year" | "month" | "day") =>
    parts.find((part) => part.type === type)?.value ?? "";
  return { year: value("year"), month: value("month"), day: value("day") };
}

function dayGroupLabel(date: Date, now: Date): string {
  if (calendarKey(date) === calendarKey(now)) return "Today";
  if (calendarKey(date) === previousCalendarKey(now)) return "Yesterday";
  return new Intl.DateTimeFormat("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year:
      calendarParts(date).year === calendarParts(now).year
        ? undefined
        : "numeric",
    timeZone: MESSAGE_TIME_ZONE,
  }).format(date);
}
